import React, { useState, useEffect } from 'react';
import { Images } from 'lucide-react';
import { GalleryEvent } from '../../types/Gallery';
import { apiClient } from '../../config/api';
import AdminTable from '../shared/AdminTable';
import AdminHeader from '../shared/AdminHeader';
import GalleryEventForm from './GalleryEventForm';
import GalleryImageManager from './GalleryImageManager';
import { galleryEventTableConfig } from './GalleryEventTableConfig';

interface GalleryEventFormData {
  title: string;
  description: string;
  date: string;
  eventType: 'Concert' | 'Competition' | 'Fundraiser' | 'Community Event' | 'Rehearsal' | 'Other';
  participatingChoirs: string[];
  location: string;
  publicGallery: boolean;
}

const GalleryManagement: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<GalleryEvent | null>(null);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedEventForImages, setSelectedEventForImages] = useState<GalleryEvent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load events and populate dynamic filter options
  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await apiClient.get('/api/admin/gallery/events');

        // Update location filter options dynamically
        const locations = Array.from(new Set(
          data
            .map((event: GalleryEvent) => event.location)
            .filter((location: string) => location && location.trim() !== '')
        ));
        galleryEventTableConfig.filterOptions.location = locations.map(location => ({
          value: location as string,
          label: location as string
        }));
      } catch (error) {
        console.error('Failed to load gallery events:', error);
      }
    };

    loadEvents();
  }, [refreshTrigger]);

  const handleAdd = () => {
    setEditingEvent(null);
    setShowForm(true);
  };

  const handleEdit = (event: GalleryEvent) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const handleManageImages = (event: GalleryEvent) => {
    setSelectedEventForImages(event);
    setShowImageManager(true);
  };

  const handleDelete = async (eventId: string) => {
    if (!window.confirm('Are you sure you want to delete this gallery event? This will also delete all associated images.')) {
      return;
    }

    try {
      setIsLoading(true);
      await apiClient.delete(`/api/admin/gallery/events/${eventId}`);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to delete gallery event:', error);
      alert('Failed to delete gallery event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = async (formData: GalleryEventFormData) => {
    try {
      setIsLoading(true);

      // Convert form data to match backend expectations
      const eventData = {
        title: formData.title,
        description: formData.description,
        date: new Date(formData.date).toISOString(),
        eventType: formData.eventType,
        participatingChoirs: formData.participatingChoirs,
        location: formData.location,
        publicGallery: formData.publicGallery
      };

      if (editingEvent) {
        await apiClient.put(`/api/admin/gallery/events/${editingEvent.id}`, eventData);
      } else {
        await apiClient.post('/api/admin/gallery/events', eventData);
      }

      setShowForm(false);
      setEditingEvent(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Failed to save gallery event:', error);
      alert('Failed to save gallery event. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventUpdate = (updatedEvent: GalleryEvent) => {
    setSelectedEventForImages(updatedEvent);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const handleCloseImageManager = () => {
    setShowImageManager(false);
    setSelectedEventForImages(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-8 space-y-6">
        {/* Header */}
        <AdminHeader
          title="Gallery Management"
          icon={<Images size={32} />}
          subtitle="Manage gallery events and their associated images"
        />

        <div className="flex justify-end">
          <button
            onClick={handleAdd}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2"
            disabled={isLoading}
          >
            <Images size={20} />
            Add Gallery Event
          </button>
        </div>

        {/* Admin Table */}
        <AdminTable<GalleryEvent>
          onView={(event) => handleManageImages(event)}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          refreshTrigger={refreshTrigger}
          config={galleryEventTableConfig}
        />

        {/* Gallery Event Form Modal */}
        <GalleryEventForm
          item={editingEvent || undefined}
          isOpen={showForm}
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
          isLoading={isLoading}
        />

        {/* Image Manager Modal */}
        {selectedEventForImages && (
          <GalleryImageManager
            galleryEvent={selectedEventForImages}
            isOpen={showImageManager}
            onClose={handleCloseImageManager}
            onEventUpdate={handleEventUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default GalleryManagement;
